/** Kingdom-overview aggregation. Pure, and driven off `PlayerObservation`.
 *
 * The overview may only show what the viewer knows: it reads the observation,
 * not `GameState`, so a town whose `detail` is null (an enemy's, or one the fog
 * only remembers) contributes nothing but its marker.
 */

import type {
  ArmyState, ContentRegistry, CreatureDef, CreatureDefId, HeroState,
  PlayerObservation, Resources, TownState,
} from "@game/core";
import { RESOURCES } from "@game/core";
import { NO_RESOURCES, addResources, townIncome } from "./townModel.js";

export interface CreatureTotal {
  readonly def: CreatureDef;
  readonly count: number;
  /** Where it is: town garrisons and hero armies, counted separately. */
  readonly inTowns: number;
  readonly inArmies: number;
}

export interface KingdomSummary {
  readonly towns: readonly TownState[];
  readonly heroes: readonly HeroState[];
  readonly armies: Readonly<Record<string, ArmyState>>;
  readonly income: Resources;
  readonly treasury: Resources;
  readonly creatures: readonly CreatureTotal[];
  readonly totalCreatures: number;
  /** Towns with a mage guild, and the highest guild level anywhere. */
  readonly bestGuildLevel: number;
}

export function summariseKingdom(
  content: ContentRegistry, obs: PlayerObservation,
): KingdomSummary {
  const towns: TownState[] = [];
  for (const t of obs.towns) if (t.detail !== null) towns.push(t.detail);
  towns.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

  const heroes: HeroState[] = [];
  for (const h of obs.heroes) {
    if (h.detail === null) continue;
    heroes.push(h.primary === null ? h.detail : { ...h.detail, primary: h.primary });
  }
  heroes.sort((a, b) => b.level - a.level || ((a.id as string) < (b.id as string) ? -1 : 1));

  // The kernel's own figure when it is there - mines and hero-carried income
  // included - and the towns' sum as the fallback for an observation built
  // without content.
  let income: Resources = NO_RESOURCES;
  if (obs.income !== null) income = obs.income;
  else for (const t of towns) income = addResources(income, townIncome(content, t));

  const inTowns = new Map<string, number>();
  const inArmies = new Map<string, number>();
  const bump = (m: Map<string, number>, def: CreatureDefId, n: number): void => {
    m.set(def as string, (m.get(def as string) ?? 0) + n);
  };

  for (const t of towns) {
    const a = obs.armies[t.garrison as string];
    if (a === undefined) continue;
    for (const slot of a.slots) if (slot !== null) bump(inTowns, slot.def, slot.count);
  }
  for (const h of heroes) {
    const a = obs.armies[h.army as string];
    if (a === undefined) continue;
    for (const slot of a.slots) if (slot !== null) bump(inArmies, slot.def, slot.count);
  }

  const ids = [...new Set([...inTowns.keys(), ...inArmies.keys()])].sort();
  const creatures: CreatureTotal[] = [];
  let totalCreatures = 0;
  for (const id of ids) {
    const def = content.creatures.get(id as CreatureDefId);
    if (def === undefined) continue;
    const t = inTowns.get(id) ?? 0;
    const a = inArmies.get(id) ?? 0;
    creatures.push({ def, count: t + a, inTowns: t, inArmies: a });
    totalCreatures += t + a;
  }
  creatures.sort((x, y) =>
    x.def.tier - y.def.tier
    || Number(x.def.upgraded) - Number(y.def.upgraded)
    || ((x.def.id as string) < (y.def.id as string) ? -1 : 1));

  let bestGuildLevel = 0;
  for (const t of towns) {
    for (const id of t.built) {
      const b = content.buildings.get(id);
      if (b !== undefined && b.guildLevel > bestGuildLevel) bestGuildLevel = b.guildLevel;
    }
  }

  return {
    towns, heroes,
    armies: obs.armies,
    income,
    treasury: obs.resources ?? NO_RESOURCES,
    creatures,
    totalCreatures,
    bestGuildLevel,
  };
}

/** Resource kinds in canonical order, for a header row. */
export const RESOURCE_ORDER = RESOURCES;
